import React, { useEffect, useMemo, useState } from 'react';
import styles from './DonutChart.module.scss';
import { useTranslation } from 'react-i18next';
import { FormatLocalizedNumber } from '../../../utils/NumberFormatter';
import { countryLocaleMappings } from '../../../constants/dataConstants';

export interface DonutChartSegment {
  value: number;
  color?: string;
  label: string;
  dataType?: string;
}

interface DonutChartProps {
  segments: DonutChartSegment[];
  size?: number; // outer square size in px
  strokeWidth?: number; // ring thickness
  gap?: number; // angle gap in degrees between segments
  legendGap?: number; // gap in px between donut and legend
  showLegend?: boolean;
  className?: string;
  isLoading?: boolean; // render skeleton shimmer like the rest of the app
  isPortfolio?: boolean;
  truncateDecimals?: boolean;
  countryCode?: string;
}

const DEFAULT_LEGEND_WIDTH = 140;

const DonutChart: React.FC<DonutChartProps> = ({
  segments,
  size = 140,
  strokeWidth = 22,
  gap = 3,
  legendGap = 8,
  showLegend = true,
  className,
  isLoading = false,
  isPortfolio = false,
  truncateDecimals = false
}) => {
  const { t } = useTranslation();
  const [animateIn, setAnimateIn] = useState(true);

  useEffect(() => {
    // run intro animation only on first mount
    const tmr = setTimeout(() => setAnimateIn(false), 900);
    return () => clearTimeout(tmr);
  }, []);

  const validSegments = useMemo(() => segments.filter((s) => Number(s.value) !== 0), [segments]);

  const total = useMemo(
    () => validSegments.reduce((sum, seg) => sum + (Number(seg.value) || 0), 0),
    [validSegments]
  );

  const hasData = useMemo(() => validSegments.length > 0 && total > 0, [validSegments, total]);

  // Ensure every segment has a color; if not, assign automatically from Data Vis palette in order
  const assignedSegments = useMemo(() => {
    const PALETTE_LENGTH = 13; // based on variables.scss --data-vis-data-vis-1..13
    return validSegments.map((s, i) => ({
      ...s,
      color: s.color ?? `var(--data-vis-data-vis-${(i % PALETTE_LENGTH) + 1})`,
    }));
  }, [validSegments]);

  const center = size / 2;
  const radius = (size - strokeWidth) / 2;

  if (!isLoading && !hasData) {
    return (
      <div
        className={[styles.wrapper, className].filter(Boolean).join(' ')}
        style={{
          width: showLegend ? size + DEFAULT_LEGEND_WIDTH : size,
          minHeight: size,
          alignItems: 'center',
          justifyContent: 'center',
          gap: legendGap,
        }}
      >
        <div>{t('noDataAvailable')}</div>
      </div>
    );
  }

  const polarToCartesian = (
    centerX: number,
    centerY: number,
    r: number,
    angleInDegrees: number
  ) => {
    const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
    return {
      x: centerX + r * Math.cos(angleInRadians),
      y: centerY + r * Math.sin(angleInRadians),
    };
  };

  const createSegmentPath = (startAngle: number, endAngle: number, r: number): string => {
    const start = polarToCartesian(center, center, r, endAngle);
    const end = polarToCartesian(center, center, r, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
    return [`M ${start.x} ${start.y}`, `A ${r} ${r} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`].join(
      ' '
    );
  };

  if (isLoading) {
    return (
      <div
        className={[styles.wrapper, className].filter(Boolean).join(' ')}
        style={{
          width: showLegend ? size + DEFAULT_LEGEND_WIDTH : size,
          gap: legendGap,
        }}
      >
        <div
          className={`${styles.singleLoader} animate`}
          role="status"
          aria-live="polite"
          aria-busy="true"
          style={{ minHeight: size }}
        />
      </div>
    );
  }

  let currentAngle = -90;

  return (
    <div
      className={[styles.wrapper, className].filter(Boolean).join(' ')}
      style={{
        width: isPortfolio ? '100%' : showLegend ? size + DEFAULT_LEGEND_WIDTH : size,
        gap: legendGap,
      }}
    >
      <svg
        className={styles.donut_chart}
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label="Donut chart"
      >
        {assignedSegments.map((segment, index) => {
          if (!segment.value) return null;
          const rawPerc = total > 0 ? (segment.value / total) * 100 : 0;
          const segmentAngle = (rawPerc / 100) * 360 - gap;
          const startAngle = currentAngle + gap / 2;
          const endAngle = startAngle + Math.max(0, segmentAngle);
          const path = createSegmentPath(startAngle, endAngle, radius);
          currentAngle = endAngle + gap / 2;

          // Path length for animation – approximate circumference portion
          const circumference = 2 * Math.PI * radius;
          const arcLength = Math.max(
            0,
            (Math.min(360, Math.max(0, endAngle - startAngle)) / 360) * circumference
          );

          return (
            <g key={index}>
              {/* optional inner border to separate segments more clearly */}
              <path
                d={path}
                fill="none"
                stroke="var(--surface-surface-tile, #fff)"
                strokeOpacity={0.8}
                strokeWidth={Math.max(strokeWidth - 2, 1)}
                strokeLinecap="round"
              />
              <path
                d={path}
                className={animateIn ? styles.draw : undefined}
                style={
                  animateIn
                    ? {
                        strokeDasharray: `${arcLength} ${circumference}`,
                        strokeDashoffset: arcLength,
                      }
                    : undefined
                }
                fill="none"
                stroke={segment.color}
                strokeWidth={strokeWidth - 6}
                strokeLinecap="round"
              />
            </g>
          );
        })}
      </svg>
      {showLegend && (
        <div className={styles.legend}>
          {assignedSegments.map((s, i) => {
            const sValue: number = truncateDecimals ? Math.round(s.value) : s.value;

            return (
              <div className={styles.legendItem} key={`legend-${i}`}>
                <span className={styles.legendDot} style={{ backgroundColor: s.color }} />
                <span className={styles.legendLabel}>{s.label}</span>
                <span className={styles.legendValue}>
                  {sValue}
                  {s.dataType === 'percentage' ? '%' : ''}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DonutChart;
